import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import { supabase } from "../supabase";
import Papa from "papaparse";

export default function FuelTolls() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeTab, setActiveTab] = useState("fuel");

  const [showImport, setShowImport] = useState(false);
  const [importType, setImportType] = useState("fuel");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");

  const [fuelRows, setFuelRows] = useState([]);
  const [tollRows, setTollRows] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");

  // ── Expanded row state (shared for both fuel and toll tabs) ──
  const [expandedRow, setExpandedRow] = useState(null);
  const [transactions, setTransactions] = useState({}); // fuel: { import_id: [...] }
  const [tollTxns, setTollTxns] = useState({}); // toll: { import_id: [...] }
  const [loadingTx, setLoadingTx] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    fetchDrivers();
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDrivers = async () => {
    const { data } = await supabase
      .from("drivers")
      .select("id, first_name, last_name, pp_device_id");
    setDrivers(data || []);
    fetchImports(data || []);
  };

  const fetchImports = async (driverList) => {
    setLoading(true);
    const { data } = await supabase
      .from("fuel_toll_imports")
      .select("*")
      .order("created_at", { ascending: false });

    const rows = data || [];
    const fuel = rows
      .filter((r) => r.type === "fuel")
      .map((r) => ({
        ...r,
        driverName: r.driver_name,
        driver: driverList.find((d) => d.id === r.driver_id) || null,
      }));
    const tolls = rows
      .filter((r) => r.type === "toll")
      .map((r) => ({
        ...r,
        driverName: r.driver_name,
        driver: driverList.find((d) => d.id === r.driver_id) || null,
      }));

    setFuelRows(fuel);
    setTollRows(tolls);
    setLoading(false);
  };

  // ── Fetch fuel transactions for a specific import row ──
  const fetchFuelTransactions = async (importId) => {
    if (transactions[importId]) return;
    setLoadingTx(importId);
    const { data } = await supabase
      .from("fuel_transactions")
      .select("*")
      .eq("import_id", importId)
      .order("transaction_date");
    setTransactions((prev) => ({ ...prev, [importId]: data || [] }));
    setLoadingTx(null);
  };

  // ── Fetch toll transactions for a specific import row ──
  const fetchTollTransactions = async (importId) => {
    if (tollTxns[importId]) return;
    setLoadingTx(importId);
    const { data } = await supabase
      .from("toll_transactions")
      .select("*")
      .eq("import_id", importId)
      .order("exit_date");
    setTollTxns((prev) => ({ ...prev, [importId]: data || [] }));
    setLoadingTx(null);
  };

  const toggleFuelRow = async (importId) => {
    if (expandedRow === importId) {
      setExpandedRow(null);
      return;
    }
    setExpandedRow(importId);
    await fetchFuelTransactions(importId);
  };

  const toggleTollRow = async (importId) => {
    if (expandedRow === importId) {
      setExpandedRow(null);
      return;
    }
    setExpandedRow(importId);
    await fetchTollTransactions(importId);
  };

  const fmt = (n) =>
    `$${parseFloat(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const findDriverByName = (name) => {
    if (!name) return null;
    const clean = name.trim().toLowerCase().replace(/\s+/g, " ");
    return (
      drivers.find(
        (d) =>
          `${d.first_name} ${d.last_name}`
            .toLowerCase()
            .replace(/\s+/g, " ") === clean
      ) || null
    );
  };

  const findDriverByPPId = (ppId) => {
    if (!ppId) return null;
    const clean = String(ppId).trim().toLowerCase();
    return (
      drivers.find(
        (d) => d.pp_device_id && d.pp_device_id.trim().toLowerCase() === clean
      ) || null
    );
  };

  // ── Parse AtoB CSV — returns summary rows + individual transactions ──
  const parseFuelCSV = (file) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          try {
            const grouped = {};
            const allTransactions = {};
            results.data.forEach((row) => {
              const name = (row["Driver Name"] || "").trim();
              if (!name) return;
              const amount = parseFloat(
                String(row["Amount"] || "0").replace(/[$,\s]/g, "")
              );
              const discount = parseFloat(
                String(row["Discount"] || "0").replace(/[$,\s]/g, "")
              );
              const txDate = (row["Transaction Date (GMT)"] || "").trim();
              const city = (row["Merchant City"] || "").trim();
              const state = (row["Merchant State"] || "").trim();
              if (!grouped[name]) {
                grouped[name] = { gross: 0, discount: 0, count: 0 };
                allTransactions[name] = [];
              }
              grouped[name].gross += amount;
              grouped[name].discount += Math.abs(discount);
              grouped[name].count += 1;
              allTransactions[name].push({
                transaction_date: txDate,
                amount: parseFloat(amount.toFixed(2)),
                discount: parseFloat(Math.abs(discount).toFixed(2)),
                merchant_city: city,
                merchant_state: state,
              });
            });
            const rows = Object.entries(grouped).map(([name, vals]) => ({
              driverName: name,
              driver: findDriverByName(name),
              gross: vals.gross,
              discount: vals.discount,
              count: vals.count,
              transactions: allTransactions[name] || [],
            }));
            resolve(rows);
          } catch (e) {
            reject(e);
          }
        },
        error: reject,
      });
    });
  };

  // ── Parse Prepass CSV — returns summary rows + individual toll transactions ──
  // Columns: F=PP Device, I=Agency, M=Exit Plaza, N=Exit Date, O=Exit Time, R=Toll Amount
  const parseTollsCSV = (file) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          try {
            const grouped = {};
            const allTollTxns = {};
            if (results.data.length > 0)
              console.log("Prepass CSV columns:", Object.keys(results.data[0]));
            results.data.forEach((row) => {
              // Columns: PPTagID=F, Agency=I, Exit_Plaza=M, Exit_Date=N, Exit_Time=O, Toll_Amount=R
              const ppId = String(
                row["PPTagID"] || row["PP Device ID"] || row["PP Device"] || ""
              ).trim();
              if (!ppId) return;
              const tollRaw =
                row["Toll_Amount"] || row["Toll Amount"] || row["Toll"] || 0;
              const toll =
                parseFloat(String(tollRaw).replace(/[^0-9.-]/g, "")) || 0;
              const agency = String(row["Agency"] || "").trim();
              const exitPlaza = String(
                row["Exit_Plaza"] || row["Exit Plaza"] || ""
              ).trim();
              const exitDate = String(
                row["Exit_Date"] || row["Exit Date"] || ""
              ).trim();
              const exitTime = String(
                row["Exit_Time"] || row["Exit Time"] || ""
              ).trim();
              if (!grouped[ppId]) {
                grouped[ppId] = { toll: 0, count: 0 };
                allTollTxns[ppId] = [];
              }
              grouped[ppId].toll += toll;
              grouped[ppId].count += 1;
              allTollTxns[ppId].push({
                pp_device_id: ppId,
                agency,
                exit_plaza: exitPlaza,
                exit_date: exitDate,
                exit_time: exitTime,
                toll: parseFloat(toll.toFixed(2)),
              });
            });
            const rows = Object.entries(grouped).map(([ppId, vals]) => ({
              ppId,
              driver: findDriverByPPId(ppId),
              toll: vals.toll,
              count: vals.count,
              tollTxns: allTollTxns[ppId] || [],
            }));
            resolve(rows);
          } catch (e) {
            reject(e);
          }
        },
        error: reject,
      });
    });
  };

  // ── Handle import → save to Supabase ──
  const handleImport = async () => {
    if (!importFile) {
      setImportError("Please select a file.");
      return;
    }
    if (!periodStart || !periodEnd) {
      setImportError("Please set the period dates.");
      return;
    }
    setImporting(true);
    setImportError("");
    try {
      if (importType === "fuel") {
        const rows = await parseFuelCSV(importFile);
        const { data: existingImports } = await supabase
          .from("fuel_toll_imports")
          .select("id")
          .eq("type", "fuel")
          .eq("period_start", periodStart)
          .eq("period_end", periodEnd);
        if (existingImports && existingImports.length > 0) {
          const ids = existingImports.map((r) => r.id);
          await supabase
            .from("fuel_transactions")
            .delete()
            .in("import_id", ids);
          await supabase.from("fuel_toll_imports").delete().in("id", ids);
        }
        const inserts = rows.map((r) => ({
          type: "fuel",
          period_start: periodStart,
          period_end: periodEnd,
          driver_id: r.driver?.id || null,
          driver_name: r.driverName,
          fuel_gross: parseFloat(parseFloat(r.gross).toFixed(2)),
          fuel_discount: parseFloat(parseFloat(r.discount).toFixed(2)),
          transaction_count: r.count,
        }));
        const { data: insertedImports } = await supabase
          .from("fuel_toll_imports")
          .insert(inserts)
          .select();
        if (insertedImports && insertedImports.length > 0) {
          const txInserts = [];
          insertedImports.forEach((imp) => {
            const matchedRow = rows.find(
              (r) => r.driverName === imp.driver_name
            );
            if (matchedRow && matchedRow.transactions.length > 0) {
              matchedRow.transactions.forEach((tx) => {
                txInserts.push({
                  import_id: imp.id,
                  driver_id: imp.driver_id,
                  ...tx,
                });
              });
            }
          });
          if (txInserts.length > 0)
            await supabase.from("fuel_transactions").insert(txInserts);
        }
        setExpandedRow(null);
        setTransactions({});
      } else {
        // ── Tolls import ──
        const tollRowsParsed = await parseTollsCSV(importFile);

        // Delete existing toll imports + transactions for this period
        const { data: existingTollImports } = await supabase
          .from("fuel_toll_imports")
          .select("id")
          .eq("type", "toll")
          .eq("period_start", periodStart)
          .eq("period_end", periodEnd);
        if (existingTollImports && existingTollImports.length > 0) {
          const ids = existingTollImports.map((r) => r.id);
          await supabase
            .from("toll_transactions")
            .delete()
            .in("import_id", ids);
          await supabase.from("fuel_toll_imports").delete().in("id", ids);
        }

        const tollInserts = tollRowsParsed.map((r) => ({
          type: "toll",
          period_start: periodStart,
          period_end: periodEnd,
          driver_id: r.driver?.id || null,
          driver_name: r.driver
            ? `${r.driver.first_name} ${r.driver.last_name}`
            : null,
          pp_device_id: r.ppId,
          toll: parseFloat(parseFloat(r.toll).toFixed(2)),
          transaction_count: r.count,
        }));

        console.log("Toll inserts to save:", tollInserts);
        const {
          data: insertedTollImports,
          error: tollInsertError,
        } = await supabase
          .from("fuel_toll_imports")
          .insert(tollInserts)
          .select();
        console.log(
          "Inserted toll imports:",
          insertedTollImports,
          "Error:",
          tollInsertError
        );

        // Insert individual toll transactions
        if (insertedTollImports && insertedTollImports.length > 0) {
          const tollTxInserts = [];
          insertedTollImports.forEach((imp) => {
            const matchedRow = tollRowsParsed.find(
              (r) => r.ppId === imp.pp_device_id
            );
            if (matchedRow && matchedRow.tollTxns.length > 0) {
              matchedRow.tollTxns.forEach((tx) => {
                tollTxInserts.push({
                  import_id: imp.id,
                  driver_id: imp.driver_id,
                  pp_device_id: tx.pp_device_id,
                  agency: tx.agency,
                  exit_plaza: tx.exit_plaza,
                  exit_date: tx.exit_date,
                  exit_time: tx.exit_time,
                  toll: tx.toll,
                });
              });
            }
          });
          if (tollTxInserts.length > 0) {
            await supabase.from("toll_transactions").insert(tollTxInserts);
          }
        }

        setExpandedRow(null);
        setTollTxns({});
      }

      await fetchImports(drivers);
      setShowImport(false);
      setImportFile(null);
      setActiveTab(importType === "fuel" ? "fuel" : "tolls");
    } catch (e) {
      setImportError("Failed to parse file: " + e.message);
    }
    setImporting(false);
  };

  // ── Delete import row ──
  const deleteRow = async (id, type) => {
    if (type === "fuel") {
      await supabase.from("fuel_transactions").delete().eq("import_id", id);
      setTransactions((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });
    } else {
      await supabase.from("toll_transactions").delete().eq("import_id", id);
      setTollTxns((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });
    }
    await supabase.from("fuel_toll_imports").delete().eq("id", id);
    if (expandedRow === id) setExpandedRow(null);
    await fetchImports(drivers);
  };

  // ── Filter rows by period ──
  const filteredFuel = fuelRows.filter((r) => {
    if (!filterStart && !filterEnd) return true;
    if (filterStart && r.period_start < filterStart) return false;
    if (filterEnd && r.period_end > filterEnd) return false;
    return true;
  });

  const filteredTolls = tollRows.filter((r) => {
    if (!filterStart && !filterEnd) return true;
    if (filterStart && r.period_start < filterStart) return false;
    if (filterEnd && r.period_end > filterEnd) return false;
    return true;
  });

  const fuelStats = filteredFuel.reduce(
    (acc, r) => ({
      gross: acc.gross + parseFloat(r.fuel_gross || 0),
      discount: acc.discount + parseFloat(r.fuel_discount || 0),
    }),
    { gross: 0, discount: 0 }
  );
  const tollStats = filteredTolls.reduce(
    (acc, r) => ({ total: acc.total + parseFloat(r.toll || 0) }),
    { total: 0 }
  );

  const tabStyle = (active) => ({
    padding: "7px 16px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "500",
    cursor: "pointer",
    border: "0.5px solid",
    borderColor: active ? "#1a1e1c" : "#e0e0e0",
    background: active ? "#1a1e1c" : "#fff",
    color: active ? "#fff" : "#6b7280",
    fontFamily: "inherit",
    transition: "all 0.15s",
  });

  const thStyle = {
    fontSize: "10px",
    fontWeight: "500",
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    padding: "8px 14px",
    textAlign: "left",
    borderBottom: "0.5px solid #e8e8e8",
    background: "#fafafa",
  };

  const tdStyle = {
    padding: "10px 14px",
    fontSize: "13px",
    color: "#111",
    borderBottom: "0.5px solid #f0f0f0",
    verticalAlign: "middle",
  };

  const badge = (type) => {
    const map = {
      matched: { bg: "#eaf3de", color: "#3b6d11", label: "Matched" },
      unmatched: { bg: "#fcebeb", color: "#a32d2d", label: "No match" },
      noppid: { bg: "#faeeda", color: "#854f0b", label: "No PP ID" },
    };
    const s = map[type] || map.unmatched;
    return (
      <span
        style={{
          background: s.bg,
          color: s.color,
          fontSize: "10px",
          fontWeight: "500",
          padding: "2px 7px",
          borderRadius: "20px",
        }}
      >
        {s.label}
      </span>
    );
  };

  const fmtDate = (d) => {
    if (!d) return "";
    const [y, m, day] = String(d).split("-");
    return new Date(
      Number(y),
      Number(m) - 1,
      Number(day)
    ).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const detailThStyle = {
    padding: "6px 12px",
    textAlign: "left",
    fontSize: "10px",
    fontWeight: "500",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "0.5px solid #e8e8e8",
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {!isMobile && <Sidebar />}
      <div className="flex-1" style={{ minWidth: 0 }}>
        {isMobile && <MobileHeader title="Fuel & Tolls" />}

        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="t-name" style={{ color: "#111", fontSize: "15px" }}>
              Fuel & Tolls
            </h1>
            <div
              style={{ fontSize: "11px", color: "#9ca3af", marginTop: "1px" }}
            >
              Import data here first, then generate settlements — fuel & toll
              will be included automatically
            </div>
          </div>
          <button
            onClick={() => {
              setShowImport(true);
              setImportError("");
            }}
            style={{
              background: "#1a1e1c",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "7px 14px",
              fontSize: "12px",
              fontWeight: "500",
              cursor: "pointer",
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            Import file
          </button>
        </div>

        <div
          style={{ padding: isMobile ? "12px" : "24px", maxWidth: "1100px" }}
        >
          {/* Import modal */}
          {showImport && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.45)",
                zIndex: 200,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={(e) => {
                if (e.target === e.currentTarget) setShowImport(false);
              }}
            >
              <div
                style={{
                  background: "#fff",
                  borderRadius: "12px",
                  width: "440px",
                  maxWidth: "95vw",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "14px 18px",
                    borderBottom: "0.5px solid #e8e8e8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#111",
                    }}
                  >
                    Import file
                  </div>
                  <button
                    onClick={() => setShowImport(false)}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "20px",
                      cursor: "pointer",
                      color: "#9ca3af",
                    }}
                  >
                    ×
                  </button>
                </div>
                <div
                  style={{
                    padding: "18px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "10px",
                        fontWeight: "500",
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: "8px",
                      }}
                    >
                      Type
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      {["fuel", "tolls"].map((t) => (
                        <label
                          key={t}
                          style={{
                            flex: 1,
                            border: `1.5px solid ${
                              importType === t ? "#3a9e5f" : "#e0e0e0"
                            }`,
                            borderRadius: "8px",
                            padding: "10px 12px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: "13px",
                            color: "#111",
                            background: importType === t ? "#f0faf4" : "#fff",
                          }}
                        >
                          <input
                            type="radio"
                            name="importType"
                            value={t}
                            checked={importType === t}
                            onChange={() => setImportType(t)}
                            style={{ accentColor: "#3a9e5f" }}
                          />
                          {t === "fuel"
                            ? "Fuel (AtoB CSV)"
                            : "Tolls (Prepass CSV)"}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "10px",
                        fontWeight: "500",
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: "8px",
                      }}
                    >
                      {importType === "fuel"
                        ? "Settlement period (week)"
                        : "Month period"}
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <input
                        type="date"
                        value={periodStart}
                        onChange={(e) => setPeriodStart(e.target.value)}
                        style={{
                          flex: 1,
                          border: "0.5px solid #e0e0e0",
                          borderRadius: "7px",
                          padding: "7px 10px",
                          fontSize: "12px",
                          outline: "none",
                          fontFamily: "inherit",
                        }}
                      />
                      <input
                        type="date"
                        value={periodEnd}
                        onChange={(e) => setPeriodEnd(e.target.value)}
                        style={{
                          flex: 1,
                          border: "0.5px solid #e0e0e0",
                          borderRadius: "7px",
                          padding: "7px 10px",
                          fontSize: "12px",
                          outline: "none",
                          fontFamily: "inherit",
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "10px",
                        fontWeight: "500",
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: "8px",
                      }}
                    >
                      File
                    </div>
                    <label
                      style={{
                        display: "block",
                        border: `1.5px dashed ${
                          importFile ? "#3a9e5f" : "#e0e0e0"
                        }`,
                        borderRadius: "8px",
                        padding: "20px",
                        textAlign: "center",
                        cursor: "pointer",
                        background: importFile ? "#f0faf4" : "#fff",
                      }}
                    >
                      <input
                        type="file"
                        accept={importType === "fuel" ? ".csv" : ".csv"}
                        style={{ display: "none" }}
                        onChange={(e) => setImportFile(e.target.files[0])}
                      />
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#9ca3af"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ display: "block", margin: "0 auto 6px" }}
                        aria-hidden="true"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <div
                        style={{
                          fontSize: "12px",
                          color: importFile ? "#3b6d11" : "#9ca3af",
                        }}
                      >
                        {importFile
                          ? importFile.name
                          : `Click to upload ${
                              importType === "fuel" ? "AtoB CSV" : "Prepass CSV"
                            }`}
                      </div>
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#c0c0c0",
                          marginTop: "2px",
                        }}
                      >
                        {importType === "fuel" ? ".csv only" : ".csv only"}
                      </div>
                    </label>
                  </div>
                  {importError && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#a32d2d",
                        background: "#fcebeb",
                        borderRadius: "7px",
                        padding: "8px 12px",
                      }}
                    >
                      {importError}
                    </div>
                  )}
                  <button
                    onClick={handleImport}
                    disabled={importing}
                    style={{
                      background: "#3a9e5f",
                      color: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      padding: "10px",
                      fontSize: "13px",
                      fontWeight: "500",
                      cursor: importing ? "default" : "pointer",
                      fontFamily: "inherit",
                      opacity: importing ? 0.7 : 1,
                    }}
                  >
                    {importing ? "Importing..." : "Import & save"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Period filter + tabs */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: "16px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                style={tabStyle(activeTab === "fuel")}
                onClick={() => setActiveTab("fuel")}
              >
                Fuel
              </button>
              <button
                style={tabStyle(activeTab === "tolls")}
                onClick={() => setActiveTab("tolls")}
              >
                Tolls
              </button>
            </div>
            <div style={{ flex: 1 }} />
            <input
              type="date"
              value={filterStart}
              onChange={(e) => setFilterStart(e.target.value)}
              style={{
                border: "0.5px solid #e0e0e0",
                borderRadius: "7px",
                padding: "6px 10px",
                fontSize: "12px",
                outline: "none",
                fontFamily: "inherit",
              }}
            />
            <input
              type="date"
              value={filterEnd}
              onChange={(e) => setFilterEnd(e.target.value)}
              style={{
                border: "0.5px solid #e0e0e0",
                borderRadius: "7px",
                padding: "6px 10px",
                fontSize: "12px",
                outline: "none",
                fontFamily: "inherit",
              }}
            />
            {(filterStart || filterEnd) && (
              <button
                onClick={() => {
                  setFilterStart("");
                  setFilterEnd("");
                }}
                style={{
                  fontSize: "11px",
                  color: "#9ca3af",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Clear
              </button>
            )}
          </div>

          {/* Info banner */}
          <div
            style={{
              background: "#eaf3de",
              borderRadius: "8px",
              padding: "10px 14px",
              fontSize: "12px",
              color: "#3b6d11",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            Import fuel & tolls before generating settlements. They will be
            automatically included in each driver's statement.
          </div>

          {/* ── FUEL TAB ── */}
          {activeTab === "fuel" && (
            <div>
              {loading ? (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#9ca3af",
                    fontSize: "13px",
                  }}
                >
                  Loading...
                </div>
              ) : filteredFuel.length === 0 ? (
                <div
                  style={{
                    background: "#fff",
                    border: "0.5px solid #e8e8e8",
                    borderRadius: "12px",
                    padding: "48px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#9ca3af",
                      marginBottom: "6px",
                    }}
                  >
                    No fuel data imported yet
                  </div>
                  <div style={{ fontSize: "12px", color: "#c0c0c0" }}>
                    Click "Import file" → select Fuel → upload AtoB CSV
                  </div>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      gap: "10px",
                      marginBottom: "16px",
                    }}
                  >
                    {[
                      { label: "Total fuel gross", val: fmt(fuelStats.gross) },
                      { label: "Total discount", val: fmt(fuelStats.discount) },
                      {
                        label: "Net fuel",
                        val: fmt(fuelStats.gross - fuelStats.discount),
                      },
                    ].map((s) => (
                      <div
                        key={s.label}
                        style={{
                          background: "#fff",
                          border: "0.5px solid #e8e8e8",
                          borderRadius: "10px",
                          padding: "12px 16px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#9ca3af",
                            marginBottom: "4px",
                          }}
                        >
                          {s.label}
                        </div>
                        <div
                          style={{
                            fontSize: "15px",
                            fontWeight: "500",
                            color: "#111",
                          }}
                        >
                          {s.val}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      background: "#fff",
                      border: "0.5px solid #e8e8e8",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead>
                        <tr>
                          {[
                            "Period",
                            "Driver",
                            "Transactions",
                            "Fuel Gross",
                            "Discount",
                            "Net Fuel",
                            "Status",
                            "",
                          ].map((h) => (
                            <th key={h} style={thStyle}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFuel.map((row, i) => (
                          <React.Fragment key={row.id}>
                            <tr
                              style={{
                                background: i % 2 === 0 ? "#fff" : "#fafafa",
                                cursor: "pointer",
                              }}
                              onClick={() => toggleFuelRow(row.id)}
                            >
                              <td
                                style={{
                                  ...tdStyle,
                                  fontSize: "11px",
                                  color: "#6b7280",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {fmtDate(row.period_start)} –{" "}
                                {fmtDate(row.period_end)}
                              </td>
                              <td style={tdStyle}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#9ca3af"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{
                                      transform:
                                        expandedRow === row.id
                                          ? "rotate(0deg)"
                                          : "rotate(-90deg)",
                                      transition: "transform 0.15s",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <polyline points="6 9 12 15 18 9" />
                                  </svg>
                                  <div>
                                    <div style={{ fontWeight: "500" }}>
                                      {row.driver_name}
                                    </div>
                                    {!row.driver_id && (
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: "#a32d2d",
                                          marginTop: "2px",
                                        }}
                                      >
                                        Not found in platform
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td style={tdStyle}>{row.transaction_count}</td>
                              <td style={tdStyle}>{fmt(row.fuel_gross)}</td>
                              <td style={{ ...tdStyle, color: "#3b6d11" }}>
                                -{fmt(row.fuel_discount)}
                              </td>
                              <td style={{ ...tdStyle, fontWeight: "500" }}>
                                {fmt(row.fuel_gross - row.fuel_discount)}
                              </td>
                              <td style={tdStyle}>
                                {badge(row.driver_id ? "matched" : "unmatched")}
                              </td>
                              <td style={tdStyle}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteRow(row.id, "fuel");
                                  }}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#9ca3af",
                                    fontSize: "16px",
                                    padding: "0 4px",
                                  }}
                                  title="Delete"
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                            {expandedRow === row.id && (
                              <tr key={`${row.id}-detail`}>
                                <td
                                  colSpan={8}
                                  style={{
                                    padding: "0",
                                    borderBottom: "0.5px solid #e8e8e8",
                                  }}
                                >
                                  <div
                                    style={{
                                      background: "#f8fbf8",
                                      padding: "12px 20px 16px 48px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        fontWeight: "500",
                                        color: "#6b7280",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.08em",
                                        marginBottom: "10px",
                                      }}
                                    >
                                      Fuel transaction detail
                                    </div>
                                    {loadingTx === row.id ? (
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#9ca3af",
                                          padding: "8px 0",
                                        }}
                                      >
                                        Loading transactions...
                                      </div>
                                    ) : (transactions[row.id] || []).length ===
                                      0 ? (
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#9ca3af",
                                          padding: "8px 0",
                                        }}
                                      >
                                        No transaction detail available.
                                        Re-import this file to capture
                                        transactions.
                                      </div>
                                    ) : (
                                      <div
                                        style={{
                                          border: "0.5px solid #e8e8e8",
                                          borderRadius: "8px",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <table
                                          style={{
                                            width: "100%",
                                            borderCollapse: "collapse",
                                            fontSize: "12px",
                                          }}
                                        >
                                          <thead>
                                            <tr
                                              style={{ background: "#f0f0f0" }}
                                            >
                                              {[
                                                "Date",
                                                "City",
                                                "ST",
                                                "Amount",
                                                "Discount",
                                              ].map((h) => (
                                                <th
                                                  key={h}
                                                  style={detailThStyle}
                                                >
                                                  {h}
                                                </th>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(transactions[row.id] || []).map(
                                              (tx, ti) => (
                                                <tr
                                                  key={ti}
                                                  style={{
                                                    background:
                                                      ti % 2 === 0
                                                        ? "#fff"
                                                        : "#fafafa",
                                                    borderBottom:
                                                      "0.5px solid #f0f0f0",
                                                  }}
                                                >
                                                  <td
                                                    style={{
                                                      padding: "6px 12px",
                                                      color: "#6b7280",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {tx.transaction_date}
                                                  </td>
                                                  <td
                                                    style={{
                                                      padding: "6px 12px",
                                                      color: "#111",
                                                    }}
                                                  >
                                                    {tx.merchant_city || "—"}
                                                  </td>
                                                  <td
                                                    style={{
                                                      padding: "6px 12px",
                                                      color: "#111",
                                                    }}
                                                  >
                                                    {tx.merchant_state || "—"}
                                                  </td>
                                                  <td
                                                    style={{
                                                      padding: "6px 12px",
                                                      color: "#111",
                                                    }}
                                                  >
                                                    {fmt(tx.amount)}
                                                  </td>
                                                  <td
                                                    style={{
                                                      padding: "6px 12px",
                                                      color: "#3b6d11",
                                                    }}
                                                  >
                                                    -{fmt(tx.discount)}
                                                  </td>
                                                </tr>
                                              )
                                            )}
                                          </tbody>
                                          <tfoot>
                                            <tr
                                              style={{
                                                background: "#f8f8f8",
                                                borderTop:
                                                  "0.5px solid #e0e0e0",
                                              }}
                                            >
                                              <td
                                                colSpan={3}
                                                style={{
                                                  padding: "6px 12px",
                                                  fontSize: "11px",
                                                  fontWeight: "500",
                                                  color: "#111",
                                                }}
                                              >
                                                Total
                                              </td>
                                              <td
                                                style={{
                                                  padding: "6px 12px",
                                                  fontSize: "11px",
                                                  fontWeight: "500",
                                                  color: "#111",
                                                }}
                                              >
                                                {fmt(row.fuel_gross)}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "6px 12px",
                                                  fontSize: "11px",
                                                  fontWeight: "500",
                                                  color: "#3b6d11",
                                                }}
                                              >
                                                -{fmt(row.fuel_discount)}
                                              </td>
                                            </tr>
                                          </tfoot>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── TOLLS TAB ── */}
          {activeTab === "tolls" && (
            <div>
              {loading ? (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#9ca3af",
                    fontSize: "13px",
                  }}
                >
                  Loading...
                </div>
              ) : filteredTolls.length === 0 ? (
                <div
                  style={{
                    background: "#fff",
                    border: "0.5px solid #e8e8e8",
                    borderRadius: "12px",
                    padding: "48px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#9ca3af",
                      marginBottom: "6px",
                    }}
                  >
                    No toll data imported yet
                  </div>
                  <div style={{ fontSize: "12px", color: "#c0c0c0" }}>
                    Click "Import file" → select Tolls → upload Prepass XLS
                  </div>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: "10px",
                      marginBottom: "16px",
                    }}
                  >
                    {[
                      { label: "Total tolls", val: fmt(tollStats.total) },
                      { label: "Records", val: filteredTolls.length },
                    ].map((s) => (
                      <div
                        key={s.label}
                        style={{
                          background: "#fff",
                          border: "0.5px solid #e8e8e8",
                          borderRadius: "10px",
                          padding: "12px 16px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#9ca3af",
                            marginBottom: "4px",
                          }}
                        >
                          {s.label}
                        </div>
                        <div
                          style={{
                            fontSize: "15px",
                            fontWeight: "500",
                            color: "#111",
                          }}
                        >
                          {s.val}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      background: "#fff",
                      border: "0.5px solid #e8e8e8",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead>
                        <tr>
                          {[
                            "Period",
                            "Driver",
                            "PP Device ID",
                            "Transactions",
                            "Total Toll",
                            "Status",
                            "",
                          ].map((h) => (
                            <th key={h} style={thStyle}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTolls.map((row, i) => (
                          <React.Fragment key={row.id}>
                            <tr
                              style={{
                                background: i % 2 === 0 ? "#fff" : "#fafafa",
                                cursor: "pointer",
                              }}
                              onClick={() => toggleTollRow(row.id)}
                            >
                              <td
                                style={{
                                  ...tdStyle,
                                  fontSize: "11px",
                                  color: "#6b7280",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {fmtDate(row.period_start)} –{" "}
                                {fmtDate(row.period_end)}
                              </td>
                              <td style={tdStyle}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#9ca3af"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{
                                      transform:
                                        expandedRow === row.id
                                          ? "rotate(0deg)"
                                          : "rotate(-90deg)",
                                      transition: "transform 0.15s",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <polyline points="6 9 12 15 18 9" />
                                  </svg>
                                  <div>
                                    <div style={{ fontWeight: "500" }}>
                                      {row.driver_name || "Unknown"}
                                    </div>
                                    {!row.driver_id && (
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: "#a32d2d",
                                          marginTop: "2px",
                                        }}
                                      >
                                        PP Device ID not matched
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td
                                style={{
                                  ...tdStyle,
                                  fontFamily: "monospace",
                                  fontSize: "12px",
                                }}
                              >
                                {row.pp_device_id}
                              </td>
                              <td style={tdStyle}>{row.transaction_count}</td>
                              <td style={{ ...tdStyle, fontWeight: "500" }}>
                                {fmt(row.toll)}
                              </td>
                              <td style={tdStyle}>
                                {badge(
                                  row.driver_id
                                    ? "matched"
                                    : row.pp_device_id
                                    ? "unmatched"
                                    : "noppid"
                                )}
                              </td>
                              <td style={tdStyle}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteRow(row.id, "toll");
                                  }}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#9ca3af",
                                    fontSize: "16px",
                                    padding: "0 4px",
                                  }}
                                  title="Delete"
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                            {expandedRow === row.id && (
                              <tr key={`${row.id}-toll-detail`}>
                                <td
                                  colSpan={7}
                                  style={{
                                    padding: "0",
                                    borderBottom: "0.5px solid #e8e8e8",
                                  }}
                                >
                                  <div
                                    style={{
                                      background: "#f8f8fb",
                                      padding: "12px 20px 16px 48px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        fontWeight: "500",
                                        color: "#6b7280",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.08em",
                                        marginBottom: "10px",
                                      }}
                                    >
                                      Toll transaction detail
                                    </div>
                                    {loadingTx === row.id ? (
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#9ca3af",
                                          padding: "8px 0",
                                        }}
                                      >
                                        Loading transactions...
                                      </div>
                                    ) : (tollTxns[row.id] || []).length ===
                                      0 ? (
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#9ca3af",
                                          padding: "8px 0",
                                        }}
                                      >
                                        No transaction detail available.
                                        Re-import this file to capture
                                        transactions.
                                      </div>
                                    ) : (
                                      <div
                                        style={{
                                          border: "0.5px solid #e8e8e8",
                                          borderRadius: "8px",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <table
                                          style={{
                                            width: "100%",
                                            borderCollapse: "collapse",
                                            fontSize: "12px",
                                          }}
                                        >
                                          <thead>
                                            <tr
                                              style={{ background: "#f0f0f0" }}
                                            >
                                              {[
                                                "PP Device",
                                                "Agency",
                                                "Exit Plaza",
                                                "Date",
                                                "Time",
                                                "Toll",
                                              ].map((h) => (
                                                <th
                                                  key={h}
                                                  style={detailThStyle}
                                                >
                                                  {h}
                                                </th>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(tollTxns[row.id] || []).map(
                                              (tx, ti) => (
                                                <tr
                                                  key={ti}
                                                  style={{
                                                    background:
                                                      ti % 2 === 0
                                                        ? "#fff"
                                                        : "#fafafa",
                                                    borderBottom:
                                                      "0.5px solid #f0f0f0",
                                                  }}
                                                >
                                                  <td
                                                    style={{
                                                      padding: "6px 12px",
                                                      color: "#6b7280",
                                                      fontFamily: "monospace",
                                                      fontSize: "11px",
                                                    }}
                                                  >
                                                    {tx.pp_device_id || "—"}
                                                  </td>
                                                  <td
                                                    style={{
                                                      padding: "6px 12px",
                                                      color: "#111",
                                                    }}
                                                  >
                                                    {tx.agency || "—"}
                                                  </td>
                                                  <td
                                                    style={{
                                                      padding: "6px 12px",
                                                      color: "#111",
                                                    }}
                                                  >
                                                    {tx.exit_plaza || "—"}
                                                  </td>
                                                  <td
                                                    style={{
                                                      padding: "6px 12px",
                                                      color: "#6b7280",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {tx.exit_date || "—"}
                                                  </td>
                                                  <td
                                                    style={{
                                                      padding: "6px 12px",
                                                      color: "#6b7280",
                                                    }}
                                                  >
                                                    {tx.exit_time || "—"}
                                                  </td>
                                                  <td
                                                    style={{
                                                      padding: "6px 12px",
                                                      color: "#a32d2d",
                                                      fontWeight: "500",
                                                    }}
                                                  >
                                                    {fmt(tx.toll)}
                                                  </td>
                                                </tr>
                                              )
                                            )}
                                          </tbody>
                                          <tfoot>
                                            <tr
                                              style={{
                                                background: "#f8f8f8",
                                                borderTop:
                                                  "0.5px solid #e0e0e0",
                                              }}
                                            >
                                              <td
                                                colSpan={5}
                                                style={{
                                                  padding: "6px 12px",
                                                  fontSize: "11px",
                                                  fontWeight: "500",
                                                  color: "#111",
                                                }}
                                              >
                                                Total
                                              </td>
                                              <td
                                                style={{
                                                  padding: "6px 12px",
                                                  fontSize: "11px",
                                                  fontWeight: "500",
                                                  color: "#a32d2d",
                                                }}
                                              >
                                                {fmt(row.toll)}
                                              </td>
                                            </tr>
                                          </tfoot>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {filteredTolls.some((r) => !r.driver_id) && (
                    <div
                      style={{
                        marginTop: "10px",
                        background: "#faeeda",
                        borderRadius: "8px",
                        padding: "10px 14px",
                        fontSize: "12px",
                        color: "#854f0b",
                      }}
                    >
                      Some drivers could not be matched. Go to the driver
                      profile → Edit → add their PP Device ID.
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
